import React from 'react';
import { Button, Input, InputNumber, Select, Tabs } from 'antd';
import { DEFAULT_HOMEPAGE_SETTINGS, normalizeHomepageSettings } from '../../../utils/homepageSettings';

export function buildHomepageTab(context) {
  const {
    shouldShowPage,
    handleSaveSystemSettings,
    updateSystemSettings,
    systemSettings,
    homepageSettingsTab,
    setHomepageSettingsTab
  } = context;

  if (!shouldShowPage('首页设置')) return null;

  const homepage = normalizeHomepageSettings(systemSettings.homepageSettings);
  const deviceKey = homepageSettingsTab === 'mobile' ? 'mobile' : 'pc';
  const device = homepage[deviceKey] || DEFAULT_HOMEPAGE_SETTINGS[deviceKey];
  const updateDevice = (patch) => updateSystemSettings((settings) => {
    const current = normalizeHomepageSettings(settings.homepageSettings);
    return {
      ...settings,
      homepageSettings: {
        ...current,
        [deviceKey]: { ...current[deviceKey], ...patch }
      }
    };
  });

  return {
    key: 'homepage_settings',
    label: '首页设置',
    children: (
      <div>
        <h3>首页设置</h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          PC / 手机可分别配置。边距为单侧百分比：0 = 刚好铺满（手机按屏宽、PC 按窗口高度）；10 = 两侧各约 10% 留白。文字倍率 1 = 与地球等宽，大于 1 可宽出地球（手机端会按文字铺满并同比调整地球）。
        </p>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={handleSaveSystemSettings}>保存设置</Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => {
              updateSystemSettings((settings) => ({
                ...settings,
                homepageSettings: JSON.parse(JSON.stringify(DEFAULT_HOMEPAGE_SETTINGS))
              }));
            }}
          >
            重置默认
          </Button>
        </div>

        <Tabs
          activeKey={homepageSettingsTab}
          onChange={setHomepageSettingsTab}
          items={[
            { key: 'pc', label: 'PC端设置' },
            { key: 'mobile', label: '手机端设置' }
          ]}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
          <div>
            <h4>进入方式</h4>
            <Select
              value={device.showSpinningEarthFirst !== false ? 'earth' : 'map'}
              onChange={(value) => updateDevice({ showSpinningEarthFirst: value === 'earth' })}
              style={{ width: '100%' }}
            >
              <Select.Option value="earth">先显示转动地球，点击大字进入</Select.Option>
              <Select.Option value="map">直接进入地图定位</Select.Option>
            </Select>
          </div>

          <div>
            <h4>品牌文字</h4>
            <Input
              value={device.brandText || '中国龙泉'}
              onChange={(e) => updateDevice({ brandText: e.target.value })}
              placeholder="中国龙泉"
              maxLength={20}
            />
          </div>

          <div>
            <h4>字号模式</h4>
            <Select
              value={device.brandFontSizeMode || 'auto'}
              onChange={(value) => updateDevice({ brandFontSizeMode: value })}
              style={{ width: '100%' }}
            >
              <Select.Option value="auto">随地球自动缩放（推荐）</Select.Option>
              <Select.Option value="fixed">固定像素大小</Select.Option>
            </Select>
          </div>

          {(device.brandFontSizeMode || 'auto') === 'auto' ? (
            <div>
              <h4>自动缩放倍率</h4>
              <InputNumber
                min={0.5}
                max={2}
                step={0.05}
                value={device.brandAutoScale ?? 1}
                onChange={(value) => updateDevice({ brandAutoScale: Number(value) || 1 })}
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                1 = 与地球等宽；大于 1 时文字可宽出地球。手机端大于 1 时按文字铺满屏宽并同比缩小地球（保存后请刷新首页）
              </p>
            </div>
          ) : (
            <div>
              <h4>固定字号 (px)</h4>
              <InputNumber
                min={24}
                max={280}
                value={device.brandFontSize || 96}
                onChange={(value) => updateDevice({ brandFontSize: Number(value) || 96 })}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <div>
            <h4>文字颜色</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={device.brandColor || '#ffffff'}
                onChange={(e) => updateDevice({ brandColor: e.target.value })}
                style={{ width: 42, height: 32, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4 }}
              />
              <Input
                value={device.brandColor || '#ffffff'}
                onChange={(e) => updateDevice({ brandColor: e.target.value })}
                placeholder="#ffffff"
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div>
            <h4>透明度 (0~1)</h4>
            <InputNumber
              min={0.05}
              max={1}
              step={0.01}
              value={device.brandOpacity ?? 0.48}
              onChange={(value) => updateDevice({ brandOpacity: value })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <h4>字间距 (em)</h4>
            <InputNumber
              min={0}
              max={1}
              step={0.02}
              value={device.brandLetterSpacing ?? (deviceKey === 'mobile' ? 0.08 : 0.22)}
              onChange={(value) => updateDevice({ brandLetterSpacing: value })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <h4>{deviceKey === 'mobile' ? '左右边距 (%)' : '上下边距 (%)'}</h4>
            <InputNumber
              min={0}
              max={35}
              step={1}
              value={device.earthMarginPercent ?? (deviceKey === 'mobile' ? 0 : 10)}
              onChange={(value) => updateDevice({
                earthMarginPercent: value == null ? (deviceKey === 'mobile' ? 0 : 10) : Number(value)
              })}
              style={{ width: '100%' }}
            />
            <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              {deviceKey === 'mobile'
                ? '0 = 刚好铺满屏宽；10 = 左右各约留 10%（地球约占宽度 80%）。保存后请刷新首页。'
                : '0 = 刚好铺满地图窗口高度；10 = 上下各约留 10%（地球约占高度 80%）。保存后请刷新首页。'}
            </p>
          </div>
        </div>

        <div style={{
          marginTop: 24,
          padding: 24,
          borderRadius: 8,
          background: 'linear-gradient(180deg, #0a1628 0%, #12243a 100%)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 12, color: '#9ab', marginBottom: 12 }}>
            {deviceKey === 'mobile' ? '手机端' : 'PC端'}预览
          </div>
          <div style={{
            fontSize: (device.brandFontSizeMode || 'auto') === 'fixed'
              ? `${Math.min(device.brandFontSize || 96, 64)}px`
              : `${Math.round(44 * (device.brandAutoScale || 1))}px`,
            fontWeight: 800,
            letterSpacing: `${device.brandLetterSpacing ?? 0.22}em`,
            color: (() => {
              const hex = device.brandColor || '#ffffff';
              const opacity = device.brandOpacity ?? 0.48;
              const raw = String(hex).replace('#', '');
              const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
              const n = parseInt(full, 16);
              if (Number.isNaN(n)) return `rgba(255,255,255,${opacity})`;
              return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${opacity})`;
            })(),
            fontFamily: '"PingFang SC", "Microsoft YaHei", sans-serif'
          }}>
            {device.brandText || '中国龙泉'}
          </div>
        </div>
      </div>
    )
  };
}
